import React from 'react';
import { captureException } from '@/utils/sentry';
import { StyleProp, Text, TextStyle } from 'react-native';

const EMPHASIS_REGEX = /<Text>(.*?)<\/Text>/gim;
const TEXT_TAG_REGEX = /<\/?Text>/gim;

interface Props {
  children: string;
  defaultStyle: StyleProp<TextStyle>;
  emphasisStyle: StyleProp<TextStyle>;
  forceSingleLine?: boolean;
}

/**
 * EMPHASIS_REGEX only matches a closed, unnested span, so anything else — a
 * dropped `</Text>` in a translation, a nested pair — falls through and gets
 * printed to the user as literal `<Text>` markup. That is how
 * `fr styled_fulfillment_on.ups_2_day` shipped. Nothing about the render told
 * anyone, so it took a diff review to find.
 *
 * Well-formed means the tags strictly alternate open/close: anything the
 * matcher below can actually express.
 */
const hasWellFormedSpans = (value: string) => {
  let depth = 0;

  for (const tag of value.match(TEXT_TAG_REGEX) ?? []) {
    depth += tag === '<Text>' ? 1 : -1;

    if (depth < 0 || depth > 1) {
      return false;
    }
  }

  return depth === 0;
};

// The markup is a property of the string, so a broken one reports on every
// render of every list row. Report each distinct string once per session.
const reportedValues = new Set<string>();

const reportMalformedSpans = (value: string) => {
  const error = new Error(
    `MultiStyleText: unbalanced <Text> spans in "${value}"`,
  );

  if (__DEV__ || process.env.NODE_ENV === 'test') {
    throw error;
  }

  if (!reportedValues.has(value)) {
    reportedValues.add(value);
    captureException(error);
  }
};

const getTextComponents = (value: string) => {
  const texts = [];

  let match;
  while ((match = EMPHASIS_REGEX.exec(value))) {
    texts.push(match);
  }

  return texts;
};

export const MultiStyleText: React.FC<Props> = ({
  children,
  defaultStyle,
  emphasisStyle,
  forceSingleLine = false,
}) => {
  if (!hasWellFormedSpans(children)) {
    reportMalformedSpans(children);

    // Strip the markup rather than print it. The emphasis is lost, but the
    // sentence still reads.
    return (
      <Text numberOfLines={forceSingleLine ? 1 : 0} style={defaultStyle}>
        {children.replace(TEXT_TAG_REGEX, '')}
      </Text>
    );
  }

  const texts = getTextComponents(children);
  const components = [];
  let lastIndex = 0;

  for (let text of texts) {
    const [match, copy] = text;
    components.push(children.substring(lastIndex, text.index));
    components.push(
      <Text key={text.index} style={emphasisStyle}>
        {copy}
      </Text>,
    );
    lastIndex = text.index + match.length;
  }

  components.push(children.substring(lastIndex));

  // add some margin
  return (
    <Text numberOfLines={forceSingleLine ? 1 : 0} style={defaultStyle}>
      {components}
    </Text>
  );
};
